import {createContext, useState, useEffect, useRef, type ReactNode, type Dispatch, type SetStateAction} from "react";
import {taruviClient} from "./taruviClient.ts";
import {User, Auth, AuthError, type UserData, type UserApp} from "@taruvi/sdk";

interface Widget {
  slug: string;
  name: string;
  icon: string;
  app_name: string;
  react_code_content?: string;
  style_code_content?: string;
  metadata?: Record<string, any>;
}

interface SpaceConfigItem {
  id: string;
  slug: string;
  appSlug: string;
  layouts: { i: string; x: number; y: number; w: number; h: number };
  react_code_content?: string;
  style_code_content?: string;
  metadata?: Record<string, any>;
}

type SpaceContextType = [
  Widget[],
  SpaceConfigItem[],
  Dispatch<SetStateAction<SpaceConfigItem[]>>,
  () => void,
  boolean,
  () => void,
  () => void,
  UserData | undefined,
  UserApp[],
];

const SpaceContext = createContext<SpaceContextType | null>(null)
const userClient = new User(taruviClient);
const authClient = new Auth(taruviClient);

function SpaceProvider({ children }: { children: ReactNode }) {
  const [widgets, setWidgets] = useState<Widget[]>([])
  const [spaceConfig, setSpaceConfig] = useState<SpaceConfigItem[]>([])
  const [me, setMe] = useState<UserData>()
  const [userApps, setUserApps] = useState<UserApp[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const prefsRef = useRef<any>(null)
  const isLoaded = useRef(false)
  const snapshotRef = useRef<SpaceConfigItem[] | null>(null)

  const enterEditMode = () => {
    snapshotRef.current = JSON.parse(JSON.stringify(spaceConfig))
    setIsEditing(true)
  }

  const cancelEditMode = () => {
    if (snapshotRef.current) setSpaceConfig(snapshotRef.current)
    snapshotRef.current = null
    setIsEditing(false)
  }

  useEffect(() => {
    const init = async () => {
      try {
        const [widgetsRes, prefsRes] = await Promise.all([
        authClient.getCurrentUser().then((res) => {
          if (!res) { authClient.login(); return []; }
          setMe(res?.data)
          return userClient.getUserApps(res.data.username).then((apps: any) => {
            setUserApps((apps.data || []).filter((a: UserApp) => !!a.url))
            return Promise.all(apps.data.map((each: any) =>
              taruviClient.httpClient.get(`api/apps/${each.slug}/widgets/`).then((res: any) => ({
                app_name: each.display_name || each.name,
                widgets: res?.data || []
              }))
            ))
          })
        }),
        userClient.getPreferences().catch((err: any) => { if (err instanceof AuthError) throw err; return null; })
      ])
      setWidgets(widgetsRes.flatMap((group: any) =>
        (group.widgets || []).map((w: any) => ({...w, app_name: group.app_name}))
      ))

      if (prefsRes?.data) {
        prefsRef.current = prefsRes.data
        const defaultWidgets = widgetsRes.flatMap((res: any) => (res.widgets || []).filter((widget: any) => widget.is_default))
        const savedConfig = Array.isArray(prefsRes?.data?.widget_config) ? prefsRes.data.widget_config : []
        const savedSlugs = new Set(savedConfig.map((c: any) => c.slug))
        const removedSlugs = new Set(savedConfig.filter((c: any) => c.is_removed).map((c: any) => c.slug))
        const newDefaults = defaultWidgets
          .filter((w: any) => !savedSlugs.has(w.slug) && !removedSlugs.has(w.slug))
          .map((w: any) => ({slug: w.slug, appSlug: w.app_slug, is_default: true, id: `${w.slug}-${Date.now()}`, layouts: {i: `${w.slug}-${Date.now()}`, x: 0, y: Infinity, w: 2, h: 2}}))
        const config = [...savedConfig, ...newDefaults]
        if (Array.isArray(config) && config.length > 0) {
          // Fetch full widget data for each unique slug
          const uniqueWidgets = [...new Map(config.filter(c => !c.is_removed).map(c => {
            const appSlug = c.appSlug || c.app_slug;
            return [`${appSlug}:${c.slug}`, {appSlug, slug: c.slug}];
          })).values()];
          const details = await Promise.all(
            uniqueWidgets.map(({appSlug, slug}) =>
              taruviClient.httpClient.get(`api/apps/${appSlug}/widgets/${slug}/`)
                .then(res => [`${appSlug}:${slug}`, (res as any).data])
                .catch((err: any) => { if (err instanceof AuthError) throw err; return [`${appSlug}:${slug}`, null]; })
            )
          );
          const detailMap = Object.fromEntries(details);

          // Hydrate saved config with fetched widget data
          const newDefaultIds = new Set(newDefaults.map((d: any) => d.id));
          const seen = new Set<string>()
          setSpaceConfig(config.map(item => {
            const appSlug = item.appSlug || item.app_slug;
            const meta = detailMap[`${appSlug}:${item.slug}`]?.metadata;
            return {
              ...item,
              appSlug,
              layouts: newDefaultIds.has(item.id) && meta
                ? {...item.layouts, w: meta.width || item.layouts.w, h: meta.height || item.layouts.h}
                : item.layouts,
              react_code_content: detailMap[`${appSlug}:${item.slug}`]?.react_code_content,
              style_code_content: detailMap[`${appSlug}:${item.slug}`]?.style_code_content,
              metadata: meta,
            };
          }).filter(item => {
            if (item.is_removed) return true
            const key = `${item.appSlug}:${item.slug}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          }))
        }
      }
      isLoaded.current = true
      } catch (err) {
        console.error('[SpaceProvider] init failed:', err?.constructor?.name, err);
        if (err instanceof AuthError) {
          authClient.login();
          return;
        }
        console.error('Failed to initialize space:', err);
      }
    }

    init()
  }, []);

  const saveWidgetConfig = () => {
    if (!isLoaded.current) return

    // Only save id, slug, and layouts — not jsx/css/metadata
    const leanConfig = spaceConfig.map(({ id, slug, layouts, appSlug, is_removed, is_default }: any) => ({ id, slug, layouts, appSlug, ...(is_removed && { is_removed }), ...(is_default && { is_default }) }));

    userClient.updatePreferences({
      ...prefsRef.current,
      widget_config: leanConfig,
    }).then(res => {
      if (res?.data) prefsRef.current = res.data
      setIsEditing(false)
    }).catch((err: any) => { if (err instanceof AuthError) { authClient.login(); return; } console.error('Failed to save widget config:', err); })
  }

  return (
    <SpaceContext.Provider value={[widgets, spaceConfig, setSpaceConfig, saveWidgetConfig, isEditing, enterEditMode, cancelEditMode, me, userApps]}>
      {children}
    </SpaceContext.Provider>
  );
}

export {SpaceContext}
export default SpaceProvider;
